import Backbone from "backbone";
import $ from "jquery";
import * as preact from "preact";
import * as _ from "underscore";

import PreactSubviewManager from "PreactSubviewManager";
import ReadingTracker from "ReadingTracker";
import * as audioPlayer from "audioPlayer";
import * as browserTTS from "browserTTS";
import * as chooseFirstLanguageDialog from "chooseFirstLanguageDialog";
import compositeViewMixin from "compositeViewMixin";
import * as currentDate from "currentDate";
import * as deviceDetect from "deviceDetect";
import * as keyboardShortcuts from "keyboardShortcuts";
import * as localAPI from "localAPI";
import * as modalDialog from "modalDialog";
import BookModel from "models/BookModel";
import UserWordModel from "models/UserWordModel";
import models from "models/models";
import * as mustcache from "mustcache";
import * as notificationBar from "notificationBar";
import * as readerPageNavigation from "readerPageNavigation";
import * as readerReviewUtils from "readerReviewUtils";
import readlangClient from "readlangClient";
import * as dateUtils from "shared/dateUtils";
import * as languageMap from "shared/languageMap";
import * as utils from "shared/utils";
import * as sidebarSettings from "sidebarExpandedSetting";
import * as signupModal from "signupModal";
import * as spanTranslator from "spanTranslator";
import * as spanUtils from "spanUtils";
import * as splitTest from "splitTest";
import * as textToSpeech from "textToSpeech";
import * as translate from "translate";
import * as transliterations from "transliterations";
import EditDocumentView from "views/EditDocumentView";
import EndOfTextView from "views/EndOfTextView";
import FormattingOptions from "views/FormattingOptions";
import LanguageSelectView from "views/LanguageSelectView";
import ReadAloudButton from "views/ReadAloudButton";
import ReaderPageDropdownMenuView from "views/ReaderPageDropdownMenuView";
import ReaderTitleView from "views/ReaderTitleView";
import ReaderWalkthroughWidget from "views/ReaderWalkthroughWidget";
import RemoteWordListToolbarView from "views/RemoteWordListToolbarView";
import RemoteWordListView from "views/RemoteWordListView";
import SmartDictionaryView from "views/SmartDictionaryView";
import StreakView from "views/StreakView";
import SyncAudioView from "views/SyncAudioView";
import TTSVoicePickerView from "views/TTSVoicePickerView";
import TranslationDirectionArrowView from "views/TranslationDirectionArrowView";
import * as walkthrough from "walkthrough";
import * as browserTTSVoices from "../browserTTSVoices";
import * as phraseHighlighter from "../readerPagePhraseHighlighter";

const sideBarWidth = 425;

let viewportCssBottom;
let layoutTimer;
let body;
let options;
let flippingPage = false;
let pageName = "readerPage";
let readerViewModel;
let navButtonsEnabled = true;
let updateLayoutDisabled = false;
let nextButton;
let prevButton;
let savedRenderedCallback;
let externalDictionaryWindow;
let showSideBarCloseHint = false;
let subviewManager;
/** Keep track of these so they can be removed when leaving page */
let eventManager = _.extend({}, Backbone.Events);
let oldSmartDictionaryProps;
let readingTracker;
let initialLayout = true;
let timerInterval;
let showingPageNavigationTooltip = false;
let readAloudButton;
let readAloudTimeout;
let sidebarAutoToggled = false;
let skipAudioButtonsShowing = false;

let ttsVoices;
let ttsVoiceSelected = false;

const ttsVoiceChangedHandler = () => {
  ttsVoiceSelected = true;
};

/**
 * This is set when you click on a highlighted word (your word) so that
 * when the sidebar is opened to the explain tab, it will have a context
 * event though the context from the current text may not have been added
 * to the UserWord
 */
let previousNewContext;
const preactSubviewManager = new PreactSubviewManager();

export const name = pageName;

const lastSpanWithCondition = (condition, backwards?: boolean) => {
  let spans = $("#pageText span[data-p], #pageText span[data-group]");
  let result = null;
  let index;

  if (backwards) {
    index = binarySearch(spans, condition, spans.length - 1, 0);
    result = spans[index];
  } else {
    index = binarySearch(spans, condition, 0, spans.length - 1);
    result = spans[index];
  }
  return result;
};

const binarySearch = (array, condition, start, end) => {
  const diff = Math.abs(end - start);

  if (diff === 0) {
    return start;
  } else if (diff === 1) {
    if (condition(array[end])) {
      return end;
    } else {
      return start;
    }
  } else {
    const mid = Math.round((start + end) / 2);

    if (condition(array[mid])) {
      return binarySearch(array, condition, mid, end);
    } else {
      return binarySearch(array, condition, start, mid);
    }
  }
};

const numberOfWordsInPage = () => {
  const firstPosition = spanTranslator.getRange(
    readerViewModel.get("firstSpan")
  ).from;
  const lastPosition = spanTranslator.getRange(
    readerViewModel.get("lastSpan")
  ).to;

  return lastPosition - firstPosition;
};

const pageTrackingProperties = () => ({
  "Words On Page": numberOfWordsInPage(),
  Public: models.bookModel.get("public"),
  Category: models.bookModel.get("category"),
  Type: models.bookModel.get("youTubeID") ? "video" : "text",
  "Owner ID": models.bookModel.get("ownerID"),
  "Total Words": models.bookModel.get("totalWords"),
  "Reader Font": models.user.get("readerFont"),
  "Reader Translation Style": models.user.get("readerTranslationStyle"),
  "Reader Word Highlights": models.user.get("readerWordHighlights"),
});

const pageRendered = () => {
  if (
    !sidebarAutoToggled &&
    sidebarSettings.persistentMode() &&
    sidebarSettings.isExpanded(models.user)
  ) {
    toggleSidebar();
  }

  if (renderedCallback) {
    renderedCallback();
    savedRenderedCallback = renderedCallback;
    renderedCallback = null;
  }

  // Create reading tracker and track page start
  if (
    !readingTracker &&
    readerViewModel.has("firstSpan") &&
    readerViewModel.get("firstSpan").length > 0 &&
    readerViewModel.has("lastSpan") &&
    readerViewModel.get("lastSpan").length > 0
  ) {
    const firstSpanPosition = spanTranslator.getRange(
      readerViewModel.get("firstSpan")
    ).from;
    resetReadingTracker(firstSpanPosition ?? 0);
    splitTest.trackEventWithProperties("pageStart", pageTrackingProperties());
  }
};

const updateUserBookPosition = _.throttle(
  async (bookModel, position, readTo) => {
    const date = await currentDate.get();

    const userBookPatch = {
      position: position,
      readTo: readTo || position,
      recentlyReadTo: readTo || position,
      lastReadDate: date.toISOString(),
    };

    const userBook = bookModel.get("userBook");

    if (userBook) {
      if (
        userBookPatch.position !== userBook.position ||
        (userBook.readTo && userBookPatch.readTo !== userBook.readTo) ||
        (userBook.recentlyReadTo &&
          userBookPatch.recentlyReadTo !== userBook.recentlyReadTo) ||
        (userBook.lastReadDate &&
          new Date(userBookPatch.lastReadDate).getTime() -
            new Date(userBook.lastReadDate).getTime() >
            2 * 60 * 1000)
      ) {
        userBookPatch.readTo = Math.max(
          userBookPatch.readTo,
          // Using truthiness of readTo to catch potential NaN
          userBook.readTo ? userBook.readTo : 0
        );
      }
      bookModel.patchUserBook(userBookPatch);
    }
  },
  1500
);

/**
 * Respond to size or layout changes.
 *
 * @param {JQueryElement} spanToRetain - This span should remain on screen
 * @param {boolean} doNotRemoveFirstLine - If true, this will not remove the first line of text in
 *                                         order to keep the spanToRetain on screen.
 *                                         Shouldn't be necessary really, just including to be safe.
 */
const updateLayout = (
  spanToRetain?: JQuery<HTMLElement>,
  doNotRemoveFirstLine?: boolean
) => {
  if (updateLayoutDisabled) {
    // This is needed because of a bug on a user's Chrome + XP machine that I can't
    // reproduce, debugging info is here:
    // https://readlang.uservoice.com/admin/tickets/18?tracking_code=83abb22bcdda2f4aa2f96d653df691d5
    return;
  }

  if ("scroll" in options) {
    $("#footer").hide();

    viewportCssBottom = 0;

    $("#pageText").css("opacity", 1);
    $("#pageTextViewport").css({
      bottom: viewportCssBottom,
    });

    pageRendered();
    return;
  } else {
    // Allow more space at bottom of screen in new gloss mode so that when translating the bottom
    // row of text, it doesn't obscure the buttons at the bottom of the page.
    const glossMode = models.user.get("readerTranslationStyle") === "glossNew";
    viewportCssBottom = 69 + (glossMode ? 20 : 0);

    $("#prev").show();

    // Remove .hover class only on touch devices. On desktop, we want to keep the hover class until
    // the user moves the mouse away from the #next or #prev button.
    if (deviceDetect.isTouchDevice()) {
      $(
        "#nextPageButton, #prevPageButton, #continueButton, #continueButtonRTL"
      ).removeClass("hover");
    }

    if (options.endOfText) {
      $("#nextPageButton").css("opacity", 0);
      $("#next").hide();
      $("#readAloudButton, .readAloudSpacer").hide();
      $("#continueButton").hide();
      $("#continueButtonRTL").hide();
    } else {
      $("#nextPageButton").css("opacity", "");
      $("#next").show();
      $("#readAloudButton, .readAloudSpacer").show();
    }
  }

  const audioFileID = models.bookModel.get("audioFileID");

  if (audioFileID) {
    audioPlayer.setupIfNecessary({
      audioFileID,
      onPositionUpdate: onAudioPositionUpdated,
      onBufferingChange: (isBuffering: boolean) =>
        readAloudButton?.setIsBuffering(isBuffering),
      audioMap: models.bookModel.get("audioMap"),
    });

    const updateAudioMap = () => {
      audioPlayer.updateAudioMap(models.bookModel.get("audioMap"));
    };

    models.bookModel.off("change:audioMap", updateAudioMap);
    models.bookModel.on("change:audioMap", updateAudioMap);
  }

  if (models.bookModel.has("youTubeID")) {
    pageRendered();
    return;
  }
  navButtonsEnabled = false;
  clearTimeout(layoutTimer);
  layoutTimer = setTimeout(
    async () => {
      if (updateLayoutDisabled) {
        return;
      }

      // set image height to half screen height max
      const image = $("#pageText img");
      if (image[0] && image.height() === 0) {
        image.on("load", () => {
          image.css({
            height: Math.min(
              (image[0] as HTMLImageElement).naturalHeight,
              0.3 * $("body").height()
            ),
          });
          setTimeout(updateLayout, 1);
        });
        if ((image[0] as HTMLImageElement).complete) {
          (image[0] as HTMLImageElement).dispatchEvent(new Event("load"));
        }
        return;
      }

      // find the last span in #pageText
      const pageTextViewport = $("#pageTextViewport");
      const pageTextViewportBottom = viewportCssBottom;
      pageTextViewport.css("bottom", pageTextViewportBottom + "px");

      const pageText = $("#pageText");
      const pageTextTop = 0;
      pageText.css("top", pageTextTop + "px");

      const viewportHeight = pageTextViewport.height();

      const spans = $("#pageText span[data-p],#pageText span[data-group]");
      let distanceFromBottom = null;

      readerViewModel.set("lastSpan", spans.last());

      if (options.to) {
        const lastSpanOrSectionBreak = $(
          "#pageText span[data-p],#pageText span[data-group], #pageText .sectionBreak"
        ).last();
        const textHeight =
          lastSpanOrSectionBreak.position().top +
          lastSpanOrSectionBreak.height();
        let processingOffScreenNodes = true;
        readerViewModel.set(
          "firstSpan",
          $(
            lastSpanWithCondition((span) => {
              const $span = $(span);

              if (processingOffScreenNodes) {
                if ($span.position().top === 0) {
                  // skip past these, seems the browser is being clever and not assigning them
                  // a position to save CPU
                  return true;
                } else {
                  processingOffScreenNodes = false;
                }
              }

              /**
               * This is the y distance between the top of $span and the end of the whole text
               * block.
               */
              const distanceFromEnd = textHeight - $span.position().top;
              if (distanceFromEnd > viewportHeight) {
                return false;
              }
              return true;
            }, true)
          )
        );

        if (readerViewModel.get("firstSpan").length === 0) {
          readerViewModel.set("firstSpan", spans.first());
        }

        appRouter.navigate(
          "library/" +
            options.bookID +
            "/from/" +
            spanTranslator.getRange(readerViewModel.get("firstSpan")).from,
          { trigger: true, replace: true }
        );
        return;
      } else {
        let bottomLimit = 0;
        if (spanToRetain) {
          // If the spanToRetain goes beyond the bottom of the viewport, let's remove one line from
          // the top of the page in order to keep it on the page.
          if (
            spanToRetain.position().top + spanToRetain.height() >
              viewportHeight &&
            !doNotRemoveFirstLine
          ) {
            removeFirstLineAndUpdate(spanToRetain);
            return;
          }

          // The "+1" is to avoid an error in Firefox where (position().top + height()) isn't
          // exactly the same for the translated word and the other words on the line
          bottomLimit = Math.min(
            viewportHeight -
              (spanToRetain.position().top + spanToRetain.height() + 1),
            0
          );
        }
        let lastSpan = $(
          lastSpanWithCondition((span) => {
            if (!span) {
              return false;
            }
            const $span = $(span);

            distanceFromBottom =
              viewportHeight - ($span.position().top + $span.height());
            if (distanceFromBottom < bottomLimit) {
              return false;
            }
            return true;
          })
        );

        // Check whether there's a section break immediately after the last span. If there is, and
        // it's outside the viewport, then look for a last span that's higher up the page so that
        // the section break will appear after a line of text on the following page.
        const lastSpanPosition = spanTranslator.getRange(lastSpan).to;

        const potentialSectionBreakButton = $(
          `#pageText .sectionBreak [data-to="${lastSpanPosition}"]`
        );
        const potentialSectionBreak =
          potentialSectionBreakButton.closest(".sectionBreak");

        if (potentialSectionBreak.length > 0) {
          const innerDistanceFromBottom =
            viewportHeight -
            potentialSectionBreak.position().top -
            potentialSectionBreak.height();

          if (innerDistanceFromBottom < bottomLimit) {
            const top = lastSpan.position().top;

            // Find the last span that's higher up the page
            lastSpan = $(
              lastSpanWithCondition((span) => {
                if (!span) {
                  return false;
                }
                const $span = $(span);

                if ($span.position().top >= top) {
                  // We don't want to as far down the page as we did before.
                  return false;
                }

                distanceFromBottom =
                  viewportHeight - ($span.position().top + $span.height());
                if (distanceFromBottom < bottomLimit) {
                  return false;
                }
                return true;
              })
            );
          }
        }

        readerViewModel.set("lastSpan", lastSpan);
        window["readerViewModel"] = readerViewModel;

        if (lastSpan.length > 0) {
          const sectionBreaks = $("#pageText .sectionBreak");
          const lastSpanTop = lastSpan.position().top;

          /** The viewport will be adjusted so that this element fits within the page */
          let adjustToElement = lastSpan;

          if (sectionBreaks.length > 0) {
            const firstSectionBreak = sectionBreaks.first();

            // Not sure whether it was intentional that this is different from `distanceFromBottom`,
            // renamed to `innerDistanceFromBottom` to make it clear that it's different.
            const innerDistanceFromBottom =
              viewportHeight -
              firstSectionBreak.position().top -
              firstSectionBreak.height();

            // If first section break is both after the last span, and within the viewport, then
            // adjust viewport to that instead
            if (
              firstSectionBreak.position().top > lastSpanTop &&
              innerDistanceFromBottom > bottomLimit
            ) {
              adjustToElement = firstSectionBreak;
            }
          }

          // Adjust height to be at bottom of the adjustToElement.
          // Important note: adjustToElement shouldn't have a top margin, since it could result in
          // it being clipped.
          pageTextViewport.css(
            "bottom",
            pageTextViewportBottom +
              (viewportHeight -
                (adjustToElement.position().top +
                  2 +
                  adjustToElement.height())) +
              "px"
          );
        }
      }

      /** These are added so that we don't reach 100% until we reach the end page. */
      const wordsForEndPage = 50;

      const { percentage, location, totalLocations } = (() => {
        const startOfPage = spanTranslator.getRange(
          readerViewModel.get("firstSpan")
        ).from;
        const endOfPage = spanTranslator.getRange(
          readerViewModel.get("lastSpan")
        ).to;
        const totalWords = models.bookModel.get("totalWords") + wordsForEndPage;
        return {
          percentage: options.endOfText
            ? 100
            : Math.round(
                (100 * startOfPage) / (totalWords - (endOfPage - startOfPage))
              ),
          location: startOfPage,
          totalLocations: totalWords,
        };
      })();

      $("#pageNumber").text(percentage + "%");

      if (location !== undefined) {
        $("#location").text(location.toLocaleString("en-US"));
        $("#totalLocations").text(totalLocations.toLocaleString("en-US"));
      }

      const newButtonWidth =
        0 -
        Math.floor(
          0.5 *
            ($("#entirePage .container").width() - $("#bookContent").width())
        );

      // resize next and prev elements
      $("#next").css({
        right: newButtonWidth + "px",
      });
      $("#prev").css({
        left: newButtonWidth + "px",
      });

      updateDictionarySize();

      if (flippingPage) {
        $("#pageText").fadeOut(0).css("opacity", 1).fadeIn(100);
        flippingPage = false;
      }
      readerViewModel.trigger("pageLayoutUpdated");

      if (!options.endOfText) {
        const lastSpanPosition = spanTranslator.getRange(
          readerViewModel.get("lastSpan")
        ).to;
        const firstSpanPosition = spanTranslator.getRange(
          readerViewModel.get("firstSpan")
        ).from;

        updateUserBookPosition(
          models.bookModel,
          firstSpanPosition,
          lastSpanPosition
        );

        if (
          lastSpanPosition >= pageData.furthestPosition ||
          "scroll" in options
        ) {
          $("#gotoLastRead").css("visibility", "hidden");
        } else {
          $("#gotoLastRead").css("visibility", "visible");
        }

        $("#footer").removeClass("showQueue");

        if (onLastPage()) {
          const isRTL =
            languageMap.map[models.bookModel.get("language")].direction ===
            "RTL";

          if (isRTL) {
            $("#continueButtonRTL").show();
            $("#prevPageButton")
              .css("visibility", "hidden")
              .addClass("lastPage");
          } else {
            $("#continueButton").show();
            $("#nextPageButton")
              .css("visibility", "hidden")
              .addClass("lastPage");
          }
        } else {
          $("#continueButton").hide();
          $("#continueButtonRTL").hide();
          $("#nextPageButton")
            .css("visibility", "visible")
            .removeClass("lastPage");
          $("#prevPageButton")
            .css("visibility", "visible")
            .removeClass("lastPage");
        }
      }

      pageRendered();
      navButtonsEnabled = true;
      initialLayout = false;
    },
    // To fix a bug where the bottom line was getting cut off only on the initial page load
    initialLayout ? 100 : 1
  );
};

const onAudioPositionUpdated = (position: number) => {
  ttsPosition = position;

  const lastSpanPosition = spanTranslator.getRange(
    readerViewModel.get("lastSpan")
  ).to;

  const firstSpanPosition = spanTranslator.getRange(
    readerViewModel.get("firstSpan")
  ).from;

  if (!isNaN(lastSpanPosition) && ttsPosition > lastSpanPosition) {
    gotoNextPage(true);
    return;
  }

  if (!isNaN(firstSpanPosition) && ttsPosition < firstSpanPosition - 1) {
    gotoPreviousPage(true);
    return;
  }

  highlightTTSPosition();
};

/**
 * Removes the first line of text and then call updateLayout()
 * This is used in order to keep a span on the screen after the user clicks to translate it.
 */
const removeFirstLineAndUpdate = (spanToRetain) => {
  // Remove the first line of text. (Hack for now, just remove a bunch of text)
  const firstSpan = readerViewModel.get("firstSpan");
  const firstSpanTop = firstSpan.position().top;

  // Remove all spans with the same top position as firstSpan
  const allSpans = $(
    "#pageText .startReviewSession, #pageText span[data-p], #pageText span[data-group]"
  );

  const spansToRemove = allSpans.filter((_index, span) => {
    return $(span).position().top <= firstSpanTop;
  });

  spansToRemove.each((_index, spanToRemove) => {
    // If there's a text node directly after the span, remove it too:
    const nextSibling = spanToRemove.nextSibling;
    if (nextSibling && nextSibling.nodeType === Node.TEXT_NODE) {
      nextSibling.remove();
    }
  });

  spansToRemove.remove();

  // Update the first span
  readerViewModel.set(
    "firstSpan",
    $("#pageText span[data-p],#pageText span[data-group]").first()
  );
  appRouter.navigate(
    "library/" +
      options.bookID +
      "/from/" +
      spanTranslator.getRange(readerViewModel.get("firstSpan")).from,
    { trigger: false, replace: true }
  );

  setTimeout(() => {
    updateLayout(spanToRetain, true);
  }, 100);
};

const onLastPage = () =>
  spanTranslator.getRange(readerViewModel.get("lastSpan")).to >=
  models.bookModel.get("totalWords") - 1;

const markAsCompleted = async () => {
  trackFinishPage();

  if (await readlangClient.isLocalMode()) {
    signupModal.show({
      headingHtml: "<h2>Sign in to manage your texts</h2>",
      models: models,
    });
    return;
  }

  models.bookModel.addTag("completed");

  if (models.bookModel.has("youTubeID")) {
    const endOfTextView = new EndOfTextView({
      model: models.bookModel,
      // @ts-ignore
      remoteUserWords: models.remoteUserWords,
      user: models.user,
      starterStory: models.bookModel.get("starterStory"),
    });
    subviewManager.addSubview("endOfTextPage", endOfTextView);
    $(".scrollViewFooter").empty().append(endOfTextView.$el);
  } else {
    appRouter.navigate("library/" + options.bookID + "/end", {
      trigger: true,
      replace: true,
    });
  }
};

const addCompletedButton = (element, text /* optional */) => {
  text =
    text ||
    '<span class="noMobile">Click when finished</span class="mobileOnly">' +
      '<span class="mobileOnly">Finished</span>';
  element.html(
    '<button class="shareButton"><i class="icon-trophy"></i> ' +
      text +
      "</button>"
  );
  element.find(".shareButton").click(() => {
    markAsCompleted();
  });
};

const showTitleBar = (instant) => {
  if (instant) {
    $("#titleBar").show();
  } else {
    $("#titleBar").fadeIn();
  }
  localStorage.setItem("reader.showTitleBar", "true");
};

const hideTitleBar = (instant) => {
  if (instant) {
    $("#titleBar").hide();
  } else {
    $("#titleBar").fadeOut();
  }
  localStorage.setItem("reader.showTitleBar", "false");
};

const isOwner = () =>
  (models.bookModel.has("ownerID") &&
    models.user.get("_id").toString() ===
      models.bookModel.get("ownerID").toString()) ||
  models.user.get("_id").toString() === "50cb58dbf939c7397b000002";

const getBookPage = async (innerOptions) => {
  // find the position
  let toPosition;

  let currentPosition = 0;
  let furthestPosition = 0;
  let spansPerPage = 1000;
  let percentage;

  const userBook = models.bookModel.get("userBook");

  if (innerOptions.endOfText) {
    return {
      user: models.user.toJSON(),
      book: models.bookModel.toJSON(),
      title: models.bookModel.get("title"),
      audio:
        models.bookModel.has("audioURL") &&
        models.bookModel.get("audioURL").length > 0,
      fromPosition: readerViewModel.get("fromPosition"),
      furthestPosition: furthestPosition,
      percentage: percentage,
      contentHTML: "", // html,
      endOfBook: innerOptions.endOfText,
      owner: isOwner(),
      allowYouTube: models.bookModel.get("metrics")?.words < 20000,
      public: models.bookModel.get("public"),
    };
  }

  if (userBook) {
    if ("readTo" in userBook) {
      furthestPosition = parseInt(userBook.readTo, 10);
    }
    if ("position" in userBook) {
      currentPosition = parseInt(userBook.position, 10);
    } else {
      currentPosition = furthestPosition;
    }

    currentPosition = Math.min(
      currentPosition,
      models.bookModel.get("totalWords") - 1
    );
  }

  if ("scroll" in innerOptions) {
    readerViewModel.set("fromPosition", 0);
    toPosition = models.bookModel.get("totalWords");
  } else {
    if (
      (innerOptions.from &&
        innerOptions.from > models.bookModel.get("totalWords") - 1) ||
      (innerOptions.to && innerOptions.to > models.bookModel.get("totalWords"))
    ) {
      delete innerOptions.from;
      innerOptions.to = models.bookModel.get("totalWords");
    }

    if ("to" in innerOptions) {
      toPosition = parseInt(innerOptions.to || 0, 10);
      readerViewModel.set(
        "fromPosition",
        Math.max(0, toPosition - spansPerPage)
      );
      percentage = "TODO: calc percentage before page render";
    } else {
      if ("from" in innerOptions) {
        readerViewModel.set(
          "fromPosition",
          parseInt(innerOptions.from || 0, 10)
        );
      } else {
        appRouter.navigate(
          "library/" + innerOptions.bookID + "/from/" + currentPosition,
          { replace: true }
        );
        readerViewModel.set("fromPosition", currentPosition);
      }
      percentage = Math.round(
        (100 * readerViewModel.get("fromPosition")) /
          models.bookModel.get("totalWords")
      );
      toPosition = readerViewModel.get("fromPosition") + spansPerPage;
    }
    if (
      readerViewModel.get("fromPosition") >= models.bookModel.get("totalWords")
    ) {
      readerViewModel.set(
        "fromPosition",
        models.bookModel.get("totalWords") - 1
      );
    }
    if (readerViewModel.get("fromPosition") > furthestPosition) {
      furthestPosition = readerViewModel.get("fromPosition");
    }
  }

  // fetch from one span before - to ensure we include any wrapping tags, e.g. <h1> or <strong>
  let htmlFragment = await models.bookModel.getHtmlFragment(
    readerViewModel.get("fromPosition") - 1,
    toPosition,
    readerReviewUtils.isActive(models)
  );

  /**
   * This is used to provide context to UserWords generated from the translations within the first
   * sentence of the text, in the case where the sentence starts before the start of the page.
   */
  const contextBeforePage =
    readerViewModel.get("fromPosition") === 0
      ? ""
      : await (async () => {
          let beforePageContextHtml = await models.bookModel.getHtmlFragment(
            Math.max(
              0,
              readerViewModel.get("fromPosition") -
                Math.floor(spanUtils.maxContextDistance / 2)
            ),
            readerViewModel.get("fromPosition"),
            false
          );
          const fromPositionIndex = beforePageContextHtml.indexOf(
            `<span data-p=${readerViewModel.get("fromPosition")}>`
          );
          beforePageContextHtml = beforePageContextHtml.substring(
            0,
            fromPositionIndex
          );
          const text = beforePageContextHtml
            .replace("</p><p>", "\n")
            .replace(/<[^>]+>/g, "");

          // Look for last sentence break
          const lastSentenceBreakIndex = text.search(
            spanUtils.lastSentenceBreakRegExp()
          );

          return utils.trimStart(text.substring(lastSentenceBreakIndex + 1));
        })();

  if (models.user.get("readerSentenceBreaks") === "on") {
    htmlFragment = htmlFragment
      // For the case where we're spannifying the punctuation
      .replace(/([?!.])<\/span>([^?!.\w<])/g, "$1</span><br/>$2")
      // For the case (old algo) where we don't spannify the punctuation
      .replace(/([?!.])([^?!.\w<])/g, "$1<br/>$2")
      // For Chinese
      .replace(/(。”?)/g, "$1<br/>");
  }

  // start on the correct fromPosition span
  // TODO: do with DOM manipulation instead of RegExp
  const startRegExp = new RegExp(
    '<div class="sectionBreak">' +
      "|(<[hp][1-9]?>.*|[^ >]*)<span data-p=" +
      readerViewModel.get("fromPosition") +
      ">"
  );
  const startIndex = htmlFragment.search(startRegExp);

  if (startIndex !== -1) {
    htmlFragment = htmlFragment.substring(startIndex);
  }

  // Add starting <p> tag if we find a closing one
  const firstClosingPTag = htmlFragment.indexOf("</p>");
  const firstOpeningPTag = htmlFragment.indexOf("<p>");
  if (
    firstClosingPTag !== -1 &&
    (firstOpeningPTag === -1 || firstOpeningPTag > firstClosingPTag)
  ) {
    htmlFragment = "<p>" + htmlFragment;
  }

  // Add ending </p> tag if we find an opening one without a matching closing one
  const lastClosingPTag = htmlFragment.lastIndexOf("</p>");
  const lastOpeningPTag = htmlFragment.lastIndexOf("<p>");
  if (lastOpeningPTag !== -1 && lastClosingPTag < lastOpeningPTag) {
    htmlFragment = htmlFragment + "</p>";
  }

  const pageData = {
    user: models.user.toJSON(),
    book: models.bookModel.toJSON(),
    title: models.bookModel.get("title"),
    audio:
      models.bookModel.has("audioURL") &&
      models.bookModel.get("audioURL").length > 0,
    fromPosition: readerViewModel.get("fromPosition"),
    furthestPosition: furthestPosition,
    percentage: percentage,
    contentHTML: htmlFragment,
    endOfBook: innerOptions.endOfText,
    owner: isOwner(),
    allowYouTube: models.bookModel.get("metrics")?.words < 20000,
    public: models.bookModel.get("public"),
    contextBeforePage,
  };

  if (
    models.user.get("subscriptionPlan") === "standard" ||
    models.user.get("subscriptionPlan") === "plus" ||
    models.user.get("subscriptionPlan") === "freeForever"
  ) {
    (pageData as any).upgraded = true;
  }

  if (pageData.book.url && pageData.book.url.length > 0) {
    (pageData as any).publicUrl = pageData.book.url;
  } else {
    pageData.book.urlExists = false;
  }

  if (pageData.public && !(pageData as any).publicUrl) {
    (pageData as any).publicUrl =
      "http://" + window.location.hostname + "/library/" + pageData.book._id;
  }

  if (await localAPI.isLocalMode()) {
    (pageData as any).localMode = true;
  }
  return pageData;
};

let pageData;
let appRouter;
let renderedCallback;
let containerElement;

const onVisibilityChange = () => {
  if (document.visibilityState === "visible") {
    // Refetch the book model in case it has changed, especially the userbook position
    const previousPosition = models.bookModel.get("userBook")?.position;

    if (previousPosition !== undefined) {
      models.bookModel.fetch({
        success: function () {
          const newPosition = models.bookModel.get("userBook")?.position;
          if (newPosition > previousPosition) {
            // The userbook position has changed, so show a notification informing the user, and
            // offering them to go to the new position
            notificationBar.showMessage({
              htmlMessage:
                "It looks like you've read more of this book on a different browser tab. " +
                `<a id="click-to-advance" href="/library/${models.bookModel.id}/from/${newPosition}">Click here to advance</a>.`,
              element: $(".container"),
              time: 1e8,
              callback: () => {
                $("#click-to-advance").on("click", () => {
                  notificationBar.dismiss();
                });
              },
            });
          }
        },
      });
    }
  }
};

export const render = async (
  _containerElement,
  _renderedCallback,
  _appRouter,
  _options
) => {
  options = _options;
  appRouter = _appRouter;
  containerElement = _containerElement;

  if (window.location.pathname.endsWith("/end")) {
    options.endOfText = true;
  }

  // Reset readingTracker
  readingTracker = undefined;

  updateLayoutDisabled = true;

  body = $("body");

  document.addEventListener("visibilitychange", onVisibilityChange);

  if (!subviewManager) {
    subviewManager = _.extend({}, compositeViewMixin);
  }

  if (!readerViewModel) {
    readerViewModel = new Backbone.Model();
  }

  subviewManager.removeSubview("wordPopUp");

  // TODO: looks broken - the body id is set in mainLayout.js now
  let initialized = false;
  if (body.attr("id") === pageName) {
    if (
      models.bookModel &&
      models.bookModel.get("_id").toString() === options.bookID.toString()
    ) {
      initialized = true;
    }
  } else {
    body.attr("id", pageName);
    renderedCallback = _renderedCallback;
  }

  if (!initialized) {
    $("#entirePage").empty();
    sidebarAutoToggled = false;
  }

  currentDictionaryHtml = undefined;
  currentDictionaryURL = undefined;

  walkthrough.setAchievement("readText", true);

  if (!initialized) {
    models.bookModel = new BookModel({ _id: options.bookID });

    browserTTSVoices.onChange(ttsVoiceChangedHandler);

    try {
      await models.bookModel.fetch();
    } catch (error) {
      const renderErrorMessage = (messageLines) => {
        containerElement.html(
          [...messageLines, '<a href="/">← Return Home</a>']
            .map((line) => `<p style="margin:15px">${line}</p>`)
            .join("\n")
        );
      };

      if (error.status === 403) {
        if (await localAPI.isLocalMode()) {
          renderErrorMessage([
            "This document doesn't belong to you.",
            "Either login, or ask the owner of the document to share it publicly.",
          ]);
          readlangClient.login();
          return;
        } else {
          renderErrorMessage([
            "This document doesn't belong to you.",
            "Ask the owner of the document to share it publicly.",
          ]);
        }
      } else {
        renderErrorMessage(["Uh oh. Article not found."]);
        splitTest.trackEvent("error-articleNotFound");
      }
      renderedCallback();
      return;
    }
  }

  if (
    models.user.get("currentLearningLanguage") !==
    models.bookModel.get("language")
  ) {
    models.user.save({
      currentLearningLanguage: models.bookModel.get("language"),
    });
  }

  if (!initialized) {
    // So that if a session has just been completed it will appear in green
    readerReviewUtils.reset();
  }

  pageData = await getBookPage(options);

  await Promise.resolve($.ready);

  if (!initialized) {
    await initializePage(_renderedCallback);
    const fieldsTriggeringPageRerender = [
      "readerSentenceBreaks",
      "readerContextTranslations",
      "readerWordHighlights",
      "readerMergePhrases",
      "readerTranslations",
      "readerTransliterations",
    ];
    eventManager.listenTo(
      models.user,
      fieldsTriggeringPageRerender.map((field) => `change:${field}`).join(" "),
      () => {
        // Re-render the page in this case
        render(containerElement, renderedCallback, appRouter, options);
      }
    );
  }

  subviewManager.subviews.syncAudioView.updateOptions({
    scroll: "scroll" in options,
  });

  $("#showTitleBar").click(() => {
    showTitleBar(false);
  });
  $("#hideTitleBar").click(() => {
    hideTitleBar(false);
  });

  if (localStorage.getItem("reader.showTitleBar") === "false") {
    hideTitleBar(true);
  }

  $("#pageText").css("opacity", 0);

  if (options.endOfText) {
    const endOfTextView = new EndOfTextView({
      model: models.bookModel,
      // @ts-ignore
      remoteUserWords: models.remoteUserWords,
      user: models.user,
    });
    subviewManager.addSubview("endOfTextPage", endOfTextView);
    $("#pageText").empty().append(endOfTextView.el);
  } else {
    // inserting the HTML this way avoids messing up the page structure in the
    // case where contentHTML contains unmatching tags
    $("#pageText").html(pageData.contentHTML);

    if (ttsPosition) {
      highlightTTSPosition();
    }

    $("#pageText .startReviewSession").click((event) => {
      event.preventDefault();
      event.stopPropagation();

      const toPosition = parseInt(
        event.currentTarget.getAttribute("data-to"),
        10
      );
      const firstSpan = readerViewModel.get("firstSpan");
      const firstSpanPosition = spanTranslator.getRange(firstSpan).from;
      appRouter.navigate(
        `/library/${options.bookID}/review/${firstSpanPosition}/${toPosition}`,
        {
          trigger: true,
        }
      );
    });
  }
  updateLayoutDisabled = false;

  if ("scroll" in options) {
    if (!(await localAPI.isLocalMode()) || models.bookModel.get("public")) {
      const shareButton = $('<div class="scrollViewFooter"></div>');
      $("#pageText").append(shareButton);
      addCompletedButton(shareButton, "I've Finished Watching");
    }

    body.addClass("scrollMode");
    $("#pageTextViewport").css({
      "overflow-y": "scroll",
      bottom: viewportCssBottom,
    });

    $("#next").hide();
    $("#prev").hide();

    readerViewModel.set(
      "firstSpan",
      $("span[data-p],span[data-group]").first()
    );
    readerViewModel.set("lastSpan", $("span[data-p],span[data-group]").last());

    hideFooter();
  } else {
    body.removeClass("scrollMode");
    showFooter();
    $("#pageTextViewport").css({
      "overflow-y": "hidden",
      bottom: viewportCssBottom,
    });

    $("#next").show();
    $("#prev").show();
  }

  $(".pageNumberContainer").click(() => {
    if (showingPageNavigationTooltip) {
      return;
    }
    // Show the tooltip
    showingPageNavigationTooltip = true;

    modalDialog.show($(".pageNavigationTooltip"), {
      center: false,
      closeButton: false,
      title: "Navigate to...",
      cancelCallback: () => {
        showingPageNavigationTooltip = false;
      },
    });
  });

  if (!options.endOfText) {
    // Initialize phrase highlighter with dependencies
    phraseHighlighter.initializePhraseHighlighter({
      subviewManager,
      readerViewModel,
      updateDictionary,
      setPreviousNewContext: (context) => {
        previousNewContext = context;
      },
      readAloudButton,
    });

    phraseHighlighter.indexSpans();

    phraseHighlighter.updateAutoHighlights();

    spanTranslator.setup({
      element: $("#pageTextViewport"),
      sourceLang: models.bookModel.get("language"),
      bookModel: models.bookModel,
      models,
      beforeTranslate: () => {
        subviewManager.removeSubview("wordPopUp");
      },
      translateDictionaryLookup: async function (userWord, span) {
        // Note 2023-06-02: This gets called after the UserWord is saved to the server, so there's a
        // slight lag even after the translation hint appears.
        updateDictionary({ userWord });
        updateLayout(span, false);

        trackReadingInteraction(span);

        // Commenting out this for now because it doesn't line up visually and feels weird
        // if (
        //   (await localAPI.isLocalMode()) &&
        //   !walkthrough.hasAchievement("readerSidebar") &&
        //   !/ /.test(userWord.get("word"))
        // ) {
        //   $("#sideBarButtonContainer .tooltip").fadeIn();
        //   $("#sideBarButtonContainer").addClass("glow");
        //   showSideBarCloseHint = true;
        //   walkthrough.setAchievement("readerSidebar", true);
        // }
      },
      userWordsList: models.remoteUserWords,
      translator: translate.getTranslator({
        models: models,
        notify: notificationBar.showMessage,
        dismissNotification: notificationBar.dismiss,
      }),
      updateLayout: updateLayout,
      speak: function (language, word) {
        if (
          !readAloudButton?.isPlaying &&
          models.user.speakingModeEnabled(language) &&
          (!subviewManager.subviews.syncAudioView ||
            !subviewManager.subviews.syncAudioView.status() ||
            !subviewManager.subviews.syncAudioView.status().playing)
        ) {
          // eslint-disable-next-line no-irregular-whitespace
          if (word.split(/[  \t.\n\r:;]/).length <= 12) {
            textToSpeech.play(language, word, () => {});
          }
        }
      },
      scrollMode: "scroll" in options,
      userWordCreator: function (wordDefinition) {
        return new UserWordModel(wordDefinition);
      },
      deleteOnUntranslate:
        models.user.get("readerDeselectBehavior") === "delete",
      contextBeforePage: pageData.contextBeforePage,
      chooseFirstLanguage: chooseFirstLanguageDialog.show,
    });

    models.bookModel.on("change:language", () => {
      // Simple way to force updating of both dictionary and browser TTS options
      window.location.reload();
      // updateDictionary();
    });

    models.user.off("change:firstLanguage", onFirstLanguageChanged);
    models.user.on("change:firstLanguage", onFirstLanguageChanged);

    models.user.on("change:readerWordHighlights", () => {
      phraseHighlighter.resetUpsellFlag();
    });

    models.user.off(
      "change:readerDeselectBehavior",
      onChangeReaderDeselectBehavior
    );
    models.user.on(
      "change:readerDeselectBehavior",
      onChangeReaderDeselectBehavior
    );

    models.user.on("change:readerReview", () => {
      window.location.reload();
    });

    models.user.off("change:readerStreakShow", initOrUpdateStreak);
    models.user.on("change:readerStreakShow", initOrUpdateStreak);
    onFirstLanguageChanged(models.user, models.user.get("firstLanguage"));
  }

  const pageTextViewport = $("#pageTextViewport");
  if (pageTextViewport.length > 0) {
    readerViewModel.set("firstSpan", $("span[data-p]").eq(0));

    if (!initialized) {
      $(window).on("resize", handleResize);
    }
    flippingPage = true;
    updateLayout();
  }

  if (!initialized) {
    initializePageNavigationButtons();
  }

  nextButton.removeClass("activated");
  prevButton.removeClass("activated");
};

const onChangeReaderDeselectBehavior = () => {
  spanTranslator.setDeleteOnUntranslate(
    models.user.get("readerDeselectBehavior") === "delete"
  );
};

const handleResize = () => {
  updateLayout();
};

const initializePage = async (_renderedCallback) => {
  if (!renderedCallback) {
    renderedCallback = _renderedCallback;
  }

  // TODO: update page title when model changes
  const languageString =
    languageMap.map[models.bookModel.get("language")].english;
  if (await localAPI.isLocalMode()) {
    $("title").text(
      `Learn ${languageString} with "${models.bookModel.get("title")}"`
    );
  } else {
    $("title").text(models.bookModel.get("title") + " - Readlang");
  }

  pageData.showReaderReviewOption = await readerReviewUtils.hasAccess(models);
  const transliteration =
    languageMap.map[models.bookModel.get("language")].transliterations?.[0];
  if (
    transliteration &&
    (await transliterations.show(models.bookModel.get("language")))
  ) {
    pageData.transliteration = utils.toTitleCase(transliteration);
  }

  pageData.selectedTab = {
    [sidebarSettings.getTab()]: true,
  };

  const html = await mustcache.renderPromise("reader", pageData);

  body.attr("id", pageName);
  body.attr("data-clickMode", "translate");

  $("#entirePage").html(html as string);

  ttsVoices = [];

  await setupFormattingOptions();

  // Intentionally not awaiting this
  addReadAloudButtonIfAppropriate().then(() => {
    // Add walkthrough widget after read aloud is initialized so we know whether it's available
    // Also, a slight delay doesn't hurt in this case to draw attention to the widget
    preactSubviewManager.add(
      "walkthroughWidget",
      preact.h(ReaderWalkthroughWidget, {
        readAloudEnabled: readAloudButton ? true : false,
      }),
      $("#walkthroughWidget")[0]
    );
  });

  if (
    (await localAPI.isLocalMode()) &&
    !walkthrough.hasAchievement("setLanguages")
  ) {
    $(".signedOutTopBar")
      .html(
        `<a href="/landing-page" data-bypass target="_blank">
          <img alt="logo" src="/content/rabbitLogo.svg" width="48" height="41" class="pointer logo">
        </a>
        <div>
        Learn ${languageString} with ${
          models.bookModel.get("category") === "Fiction" ? "stories" : "texts"
        } like
      ${models.bookModel.get("title")} on Readlang. Click to translate words and phrases and practice later
      with flashcards.
      <a href="#" class="signin">Sign in</a>
      or <a href="/landing-page" data-bypass target="_blank">Learn more</a>.
      </div>
      <div>
        <i class="icon icon-remove signedOutCloseButton"></i>
      </div>
      `
      )
      .css("display", "flex");

    $(".signedOutCloseButton").click(() => {
      $(".signedOutTopBar").hide();
    });

    if (!localStorage?.getItem("referrer")) {
      localStorage?.setItem("referrer", "readerPage");
    }
  }

  if (
    !["plus", "freeForever"].includes(models.user.get("subscriptionPlan")) &&
    models.user.get("featureFlags")?.["betterAIModel"]
  ) {
    notificationBar.showMessage({
      htmlMessage:
        "Thanks for trying out the better AI model option based on GPT-4o! If you want " +
        "to continue using this, please <a href='/account'>upgrade to Premium Plus</a>. Otherwise, " +
        "you will now revert to using the default GPT-4o mini model.",
      time: 9999999,
    });
    models.user.setFeature("betterAIModel", false);
  }

  $(".dismissSidebarHint").click(dismissSidebarHint);

  $(".addToQueue").click(async () => {
    if (await readlangClient.isLocalMode()) {
      signupModal.show({
        headingHtml: "<h2>Sign in to manage your texts</h2>",
        models: models,
      });
      return;
    }
    models.bookModel.addTag("queue", undefined, undefined);
    $("#footer").removeClass("showQueue");
  });

  if (localStorage.getItem("zombieMode")) {
    $("#pageText").html(
      $("#pageText")
        .html()
        .replace(/<span data-position=\d+>/g, "")
        .replace(/<span data-p=\d+>/g, "")
        .replace(/<\/span>/g, "")
    );
    pageRendered();
    return;
  }

  let startPosition;
  if (models.bookModel.get("totalWords") >= 650) {
    startPosition = models.bookModel.get("userBook")?.position;
    if (startPosition) {
      startPosition = Math.max(0, startPosition - 10);
    }
  }

  subviewManager.addSubview(
    "syncAudioView",
    new SyncAudioView({
      el: '.tabContent[data-tab="youTube"]',
      mediaPanel: $("#mediaPanel"),
      model: models.bookModel,
      readerViewModel,
      spanTranslator,
      appRouter,
      scroll: "scroll" in options,
      position: options.scroll || readerViewModel.get("fromPosition"),
      mediaPanelHeight,
      gotoPosition: setSyncAudioPosition,
      startPosition: startPosition || 0,
      setMediaPanelHeight,
      showFooter,
      hideFooter,
      setPageMode,
      owner: isOwner(),
      textDirection:
        languageMap.map[models.bookModel.get("language")].direction || "LTR",
      markAsCompleted,
    })
  );

  preactSubviewManager.add(
    "titleView",
    preact.h(ReaderTitleView, {
      readerViewModel: readerViewModel,
      onSectionChange: (start: number) => {
        appRouter.navigate(
          "/library/" + models.bookModel.id + "/from/" + start,
          { trigger: true }
        );
      },
    }),
    $("#title")[0]
  );

  eventManager.listenTo(models.user, "change:currentLearningLanguage", () => {
    if (/^(on|readyToLearn)$/.test(models.user.get("readerWordHighlights"))) {
      render(containerElement, renderedCallback, appRouter, options);
    }
  });

  if (!(await localAPI.isLocalMode())) {
    subviewManager.addSubview(
      "remoteWordListView",
      new RemoteWordListView({
        model: models.remoteUserWords,
        models: models,
        wordGroup: "",
        showCancelButton: false,
        noWordsMessage: "Click any word on the left to translate.",
      })
    );

    subviewManager.addSubview(
      "remoteWordListToolbarView",
      new RemoteWordListToolbarView({
        model: models.remoteUserWords,
        models: models,
        appRouter: appRouter,
      })
    );

    $('.tabContent[data-tab="editWord"]')
      .empty()
      .append(subviewManager.subviews.remoteWordListToolbarView.$el)
      .append(subviewManager.subviews.remoteWordListView.$el);

    eventManager.listenTo(
      models.remoteUserWords,
      "change:translation",
      (model) => {
        spanTranslator.updateTranslations(
          model.get("word"),
          model.get("translation")
        );
      }
    );
  } else {
    $('.tabContent[data-tab="editWord"]')
      .empty()
      .html(
        `<p style="margin-left: 10px;margin-right: 10px;">
          <a href="#" class="signin">Sign up for free</a>
          to view and manage your words and phrases.
        </p>`
      );

    $(".signin").click(() => {
      signupModal.show({
        // headingHtml: "<h2>Sign in to save your words</h2>",
        models: models,
      });
      return false;
    });
  }

  if (languageMap.map[models.bookModel.get("language")].direction === "RTL") {
    $("#pageText").attr("dir", "RTL");
  }

  preactSubviewManager.add(
    "dropdownMenu",
    preact.h(ReaderPageDropdownMenuView, { appRouter, readerViewModel }),
    $("#dropdownMenu")[0]
  );

  // Sidebar
  $("#sideBarButton").click(() => {
    toggleSidebar();
  });

  const sidebar = $(".sideBar");

  sidebar.find(".tabHeader").click(function () {
    const $this = $(this);
    $(".tabHeader").removeClass("selected");
    $(".tabContent").removeClass("selected");

    sidebarSettings.setTab($this.attr("data-tab"));

    $this.addClass("selected");
    showContentTab();
  });

  initOrUpdateStreak();
};

const addReadAloudButtonIfAppropriate = async () => {
  ttsPosition = undefined;

  if (models.bookModel.has("youTubeID")) {
    return;
  }

  const initResult = await browserTTS.initialize(
    models.bookModel.get("language")
  );

  if (initResult.voiceSelected) {
    ttsVoiceSelected = initResult.voiceSelected;
  }
  ttsVoices = initResult.ttsVoices;

  if (ttsVoices.length === 0 && !models.bookModel.has("audioFileID")) {
    // No need to add button
    return;
  }

  $(
    ".readAloudSpacer, .readAloudButtonContainer, .readAloudRateSetting"
  ).fadeIn();

  if (models.bookModel.has("audioFileID")) {
    $(".readAloudRateHeading").text("Audio Speed");
  } else {
    $(".readAloudRateHeading").text("Read Aloud Speed");
    $(".readAloudVoiceSetting").fadeIn();
  }

  await setupFormattingOptions();

  const readAloudButtonElement = $("#readAloudButton");
  readAloudButton = new ReadAloudButton({
    el: readAloudButtonElement,
    onPlay: () => {
      playReadAloud();
    },
    onPause: () => {
      pauseReadAloud();
    },
  });
  subviewManager.addSubview("readAloudButton", readAloudButton);

  $(".skipBackward").click(jumpBackward);
  $(".skipForward").click(jumpForward);

  if (
    models.bookModel.has("audioFileID") &&
    parseInt(options.from ?? 0, 10) === 0
  ) {
    modalDialog.show($(".playAudioTooltip"), {
      center: false,
      closeButton: false,
      title: "Navigate to...",
      cancelCallback: () => {
        showingPageNavigationTooltip = false;
      },
    });
  }

  if (readAloudButtonElement.length > 0) {
    keyboardShortcuts.registerHandler(
      "readerPage",
      "l".charCodeAt(0),
      jumpForward
    );
    keyboardShortcuts.registerHandler(
      "readerPage",
      "L".charCodeAt(0),
      jumpForward
    );
    keyboardShortcuts.registerHandler(
      "readerPage",
      keyboardShortcuts.keyCodes.down,
      jumpForward
    );

    keyboardShortcuts.registerHandler(
      "readerPage",
      "j".charCodeAt(0),
      jumpBackward
    );
    keyboardShortcuts.registerHandler(
      "readerPage",
      "J".charCodeAt(0),
      jumpBackward
    );
    keyboardShortcuts.registerHandler(
      "readerPage",
      keyboardShortcuts.keyCodes.up,
      jumpBackward
    );
  }
};

const setupFormattingOptions = async () => {
  const transliteration =
    languageMap.map[models.bookModel.get("language")].transliterations?.[0];

  preactSubviewManager.remove("formattingOptions");
  preactSubviewManager.add(
    "formattingOptions",
    preact.h(FormattingOptions, {
      transliteration: transliteration
        ? utils.toTitleCase(transliteration)
        : undefined,
      showReaderReviewOption: await readerReviewUtils.hasAccess(models),
      ttsVoices,
      restartTTSIfPlayingOrPlayDemo: () => {
        if (readAloudButton?.isPlaying) {
          // Restart TTS with new voice
          pauseReadAloud();
          // Use timeout to avoid a bug in Chrome where the audio doesn't play if we call playPause
          // immediately
          setTimeout(() => {
            playReadAloud();
          }, 50);
        } else {
          // TTS wasn't playing, so play the example text
          browserTTS.playVoiceDemo(models.bookModel.get("language"));
        }
      },
      audioFileMode: models.bookModel.has("audioFileID"),
    }),
    $(".tabContent[data-tab='formattingOptions']")[0]
  );
};

const jumpForward = () => {
  // Jump forward 10 seconds
  if (models.bookModel.get("audioFileID")) {
    audioPlayer.jump(10);
  } else {
    jumpReadAloud(8);
  }
};

const jumpBackward = () => {
  // Jump backwards 10 seconds
  if (models.bookModel.get("audioFileID")) {
    audioPlayer.jump(-10);
  } else {
    jumpReadAloud(-8);
  }
};

const gotoPreviousPage = (keepAudioPlaying?: boolean) => {
  previousUserWord = undefined;
  showSmartDictionaryIfAppropriate();

  if (!keepAudioPlaying) {
    if (readAloudButton?.isPlaying) {
      pauseReadAloud();
    }
    ttsPosition = undefined;
  }

  if (options.endOfText) {
    appRouter.navigate(
      "library/" + options.bookID + "/to/" + models.bookModel.get("totalWords"),
      {
        trigger: true,
      }
    );
    return;
  }
  if (!navButtonsEnabled) {
    return;
  }
  if (spanTranslator.getRange(readerViewModel.get("firstSpan")).from <= 0) {
    notificationBar.showMessage({
      message: "You're on the first page.",
      element: $(".container"),
    });
  } else {
    delete options.from;
    options.to = Math.max(
      0,
      spanTranslator.getRange(readerViewModel.get("firstSpan")).from - 1
    );

    prevButton.addClass("activated");

    renderedCallback = savedRenderedCallback;
    appRouter.navigate("library/" + options.bookID + "/to/" + options.to, {
      trigger: true,
    });
  }
};

const gotoNextPage = async (keepAudioPlaying = false) => {
  walkthrough.setAchievement("turnPage", true);

  previousUserWord = undefined;
  showSmartDictionaryIfAppropriate();

  if (options.endOfText) {
    return;
  }

  if (!keepAudioPlaying) {
    if (readAloudButton?.isPlaying) {
      pauseReadAloud();
    }
    ttsPosition = undefined;
  }

  // Add to queue if not already there
  if (
    !(await readlangClient.isLocalMode()) &&
    !models.bookModel.hasTag("queue") &&
    !models.bookModel.hasTag("completed") &&
    !onLastPage()
  ) {
    models.bookModel.addTag("queue", undefined, undefined);
    $("#footer").removeClass("showQueue");
  }

  trackFinishPage();

  if (!navButtonsEnabled) {
    return;
  }
  if (onLastPage()) {
    markAsCompleted();
    return;
  }
  const spans = $("span[data-p],span[data-group]");
  if (
    pageData.endOfBook &&
    spans.index(readerViewModel.get("lastSpan")) === spans.length - 1
  ) {
    notificationBar.showMessage({
      message: "You're on the last page.",
      element: $(".container"),
    });
    return;
  }
  delete options.to;
  options.from =
    spanTranslator.getRange(readerViewModel.get("lastSpan")).to + 1;

  nextButton.addClass("activated");

  renderedCallback = savedRenderedCallback;
  appRouter.navigate("library/" + options.bookID + "/from/" + options.from, {
    trigger: true,
  });
};

/**
 * @param {"smartDictionary" | "dictionary" | "editWord"} tabName
 */
const toggleTab = async (tabName) => {
  const tabHeader = $(`.sideBar .tabHeader[data-tab="${tabName}"]`);

  if (sideBarVisible && tabHeader.hasClass("selected")) {
    // Hide sidebar
    toggleSidebar();
    return;
  }

  if (!sideBarVisible) {
    await new Promise<void>((resolve) => toggleSidebar(() => resolve()));
  }

  if (!tabHeader.hasClass("selected")) {
    tabHeader.click();
  }
};

const initializePageNavigationButtons = () => {
  nextButton = $("#next");
  prevButton = $("#prev");

  if (models.user.hasFeature("disableMarginButtons")) {
    for (const button of [nextButton, prevButton]) {
      button.css({
        top: "auto",
        height: "80px",
        bottom: "0",
      });
    }
  }

  keyboardShortcuts.registerHandler(
    "readerPage",
    keyboardShortcuts.keyCodes.left,
    () => {
      if (!models.bookModel.has("youTubeID")) {
        $("#prev").click();
      }
    }
  );
  keyboardShortcuts.registerHandler(
    "readerPage",
    keyboardShortcuts.keyCodes.right,
    () => {
      if (!models.bookModel.has("youTubeID")) {
        $("#next").click();
      }
    }
  );
  keyboardShortcuts.registerHandler(
    "readerPage",
    keyboardShortcuts.keyCodes.escape,
    () => {
      $(".yourWordPopUp .closeDialogButton").click();
    }
  );
  if (readAloudButton) {
    const playPauseHandler = (event) => {
      if (models.bookModel.has("youTubeID")) {
        return;
      }

      if (event.target.id === "readAloudButton") {
        // Don't toggle read aloud if the user pressed space while the button was focused, since
        // it will result in cancelling the toggle
        return;
      }

      readAloudButton.toggle();
    };
    keyboardShortcuts.registerHandler(
      "readerPage",
      keyboardShortcuts.keyCodes.space,
      playPauseHandler
    );
    keyboardShortcuts.registerHandler(
      "readerPage",
      "k".charCodeAt(0),
      playPauseHandler
    );
    keyboardShortcuts.registerHandler(
      "readerPage",
      "K".charCodeAt(0),
      playPauseHandler
    );
  }

  keyboardShortcuts.registerHandler("readerPage", "e".charCodeAt(0), () => {
    toggleTab("smartDictionary");
  });
  keyboardShortcuts.registerHandler("readerPage", "E".charCodeAt(0), () => {
    toggleTab("smartDictionary");
  });
  keyboardShortcuts.registerHandler("readerPage", "d".charCodeAt(0), () => {
    toggleTab("dictionary");
  });
  keyboardShortcuts.registerHandler("readerPage", "D".charCodeAt(0), () => {
    toggleTab("dictionary");
  });
  keyboardShortcuts.registerHandler("readerPage", "w".charCodeAt(0), () => {
    toggleTab("editWord");
  });
  keyboardShortcuts.registerHandler("readerPage", "W".charCodeAt(0), () => {
    toggleTab("editWord");
  });

  const isRTL =
    languageMap.map[models.bookModel.get("language")].direction === "RTL";

  if (isRTL) {
    nextButton.attr("title", "Previous Page");
    prevButton.attr("title", "Next Page");

    $("#nextPageButton").attr("title", "Previous Page");
    $("#prevPageButton").attr("title", "Next Page");
  }

  nextButton.on("mouseenter", () => {
    $("#nextPageButton").addClass("hover");

    if (!isRTL) {
      $("#continueButton").addClass("hover");
    }
  });
  nextButton.on("mouseleave", () => {
    $("#nextPageButton").removeClass("hover");
    $("#continueButton").removeClass("hover");
  });
  prevButton.on("mouseenter", () => {
    $("#prevPageButton").addClass("hover");

    if (isRTL) {
      $("#continueButtonRTL").addClass("hover");
    }
  });
  prevButton.on("mouseleave", () => {
    $("#prevPageButton").removeClass("hover");
    $("#continueButtonRTL").removeClass("hover");
  });

  nextButton.click(() => {
    if (isRTL) {
      gotoPreviousPage();
    } else {
      gotoNextPage();
    }
  });
  prevButton.click(() => {
    if (isRTL) {
      gotoNextPage();
    } else {
      gotoPreviousPage();
    }
  });

  $("#home").click((event) => {
    if (
      onLastPage() &&
      !models.bookModel.hasTag("completed") &&
      !models.bookModel.has("youTubeID")
    ) {
      if (
        !confirm(
          "Are you sure you want to leave before continuing to the last page? Click OK to leave " +
            "or Cancel to stay."
        )
      ) {
        event.stopPropagation();
        event.preventDefault();
      }
    }
  });

  $("#nextPageButton, #continueButton").click(() => {
    nextButton.click();
  });
  $("#prevPageButton, #continueButtonRTL").click(() => {
    prevButton.click();
  });
  $("#pageNavigationStart").on("click", (event) => {
    event.preventDefault();
    event.stopPropagation();

    pauseReadAloud(true);
    modalDialog.cancelAll();
    readerPageNavigation.goToLocation("from", 0, appRouter);
  });
  $("#pageNavigationFurthest").on("click", () => {
    event.preventDefault();
    event.stopPropagation();

    pauseReadAloud(true);
    modalDialog.cancelAll();
    readerPageNavigation.goToLocation(
      "to",
      pageData.furthestPosition,
      appRouter
    );
  });
  $("#pageNavigationSpecific").on("click", () => {
    event.preventDefault();
    event.stopPropagation();

    pauseReadAloud(true);
    readerPageNavigation.showSpecificLocationModal(appRouter, readerViewModel);
  });
};

const pauseReadAloud = (resetPosition?: boolean) => {
  readAloudButton?.setIsPlaying(false);
  if (resetPosition) {
    ttsPosition = undefined;
  }
  clearTimeout(readAloudTimeout);

  if (models.bookModel.has("audioFileID")) {
    audioPlayer.pause();
    return;
  }
  browserTTS.cancelTTS();
};

const jumpReadAloud = (amount: number) => {
  const isPlaying = readAloudButton.isPlaying;
  pauseReadAloud();
  ttsPosition = Math.max(0, ttsPosition + amount);

  const firstSpanPosition = spanTranslator.getRange(
    readerViewModel.get("firstSpan")
  ).from;

  if (ttsPosition < firstSpanPosition) {
    const rememberTTSPosition = ttsPosition;
    // Ideally we'd want to continue playing here, but we first need to fetch the previous page's
    // contents and render it so simpler just to stay paused in this case
    gotoPreviousPage();
    ttsPosition = rememberTTSPosition;
    highlightTTSPosition();
    return;
  }

  highlightTTSPosition();
  if (isPlaying) {
    setTimeout(() => playReadAloud(), 100);
  }
};

const playReadAloud = () => {
  walkthrough.setAchievement("readAloud", true);
  readAloudButton.setIsPlaying(true);
  const firstSpanPosition = spanTranslator.getRange(
    readerViewModel.get("firstSpan")
  ).from;

  startPlayingFrom(
    ttsPosition !== undefined /*&& ttsPosition < lastSpanPosition*/
      ? ttsPosition
      : firstSpanPosition
  );
};

let ttsPosition = undefined;

const highlightTTSPosition = () => {
  $("span[data-p]").removeClass("audioPosition");
  const currentSpan = $('span[data-p="' + ttsPosition + '"]');
  currentSpan.addClass("audioPosition");
};

const startPlayingFrom = (position) => {
  if (!skipAudioButtonsShowing) {
    $(".skipBackward, .skipForward").fadeIn();
    skipAudioButtonsShowing = true;
  }

  if (position >= models.bookModel.get("totalWords")) {
    // Finished text
    pauseReadAloud();

    // Remove audio position highlight
    $("span[data-p]").removeClass("audioPosition");
    return;
  }

  if (models.bookModel.has("audioFileID")) {
    const playbackRate = parseFloat(
      browserTTSVoices.getRate(models.bookModel.get("language"))
    );
    audioPlayer.play(position, playbackRate);
    return;
  }

  const contextSentenceNodes = spanTranslator.getSentenceFromPosition(position);

  (contextSentenceNodes as any[]).forEach((node) => {
    if (Array.isArray(node.node)) return;
    node.node = [node.node];
  });

  const playTTS = () => {
    browserTTS.playTTS(
      models.bookModel.get("language"),
      contextSentenceNodes as any,
      onAudioPositionUpdated,
      (nextPosition) => {
        clearTimeout(readAloudTimeout);
        readAloudTimeout = setTimeout(() => {
          startPlayingFrom(nextPosition);
          onAudioPositionUpdated(nextPosition);
        }, 300);
      },
      () => {
        // On error, just stop playing
        pauseReadAloud();
      }
    );
  };

  if (ttsVoiceSelected) {
    playTTS();
    return;
  }

  modalDialog.showPreactComponent(
    preact.h(TTSVoicePickerView, {
      title: "Select a voice",
      ttsVoices,
      language: models.bookModel.get("language"),
      close: () => {
        modalDialog.cancelAll();
        playTTS();
      },
    })
  );
};

const onFirstLanguageChanged = (model, value) => {
  updateDictionary({});
  if (languageMap.map[value].alphabet) {
    body.removeClass("basicLatinFirstLanguage");
  } else {
    body.addClass("basicLatinFirstLanguage");
  }
};

const setPageMode = (pageMode, position) => {
  switch (pageMode) {
    case "scroll":
      if (!("scroll" in options)) {
        position =
          position || options.scroll || options.from || options.to || 0;
        appRouter.navigate(
          "library/" + options.bookID + "/scroll/" + position,
          { trigger: true, replace: true }
        );
      }
      break;
    case "page":
      break;
    default:
  }
};

const trackReadingInteraction = (span) => {
  if (!readingTracker) {
    return;
  }

  let range;
  try {
    range = spanTranslator.getRange(span);
  } catch (error) {
    // This can happen if the user has clicked to dismiss a translation hint before this function
    // gets called.
    return;
  }
  const endPosition = range.to;
  readingTracker.reportInteraction(endPosition);
};

const logWordsRead = async (readingStats) => {
  if (!(await readlangClient.isLocalMode())) {
    // Send tracking info
    readlangClient.request(
      "POST",
      "/api/userLearningLog/read",
      {
        wordsRead: readingStats.wordsRead,
        timeTaken: Math.round(readingStats.timeReading / 1000),
        language: models.bookModel.get("language"),
        timezoneOffset: dateUtils.getTimezoneOffset(),
      },
      async () => {
        if (await models.user.hasNewStreakFeature()) {
          models.streak.fetch();
        }
      }
    );
  }
};

const trackFinishPage = async () => {
  if (!readingTracker) {
    return;
  }

  // Track last interaction on the last span
  trackReadingInteraction(readerViewModel.get("lastSpan"));

  const readingStats = readingTracker.getStats();

  if (readingStats.fractionWordsRead < 0.67) {
    // Didn't read more than 2/3 of the words, so don't track
  } else {
    // Track reading stats
    const seconds = Math.round(readingStats.timeReading / 1000);

    // XXX Rename WPM to readingSpeed for consistency
    models.user.updateReadingSpeed(
      models.bookModel.get("language"),
      readingStats.WPM
    );

    logWordsRead(readingStats);

    splitTest.trackEventWithProperties("pageFinish", {
      Words: readingStats.wordsRead,
      Time: seconds,
      "Reading Speed": readingStats.wordsRead / (seconds / 60),
      "Fraction Read": readingStats.fractionWordsRead,
      ...pageTrackingProperties(),
    });
  }

  readingTracker = undefined;
};

const initOrUpdateStreak = async () => {
  if (await models.user.hasNewStreakFeature()) {
    await models.streak.fetch();
    const goalMetToday = models.streak.get("todayStats")?.goalMet;
    const streakShow = models.user.get("readerStreakShow") ?? "always";

    if (
      streakShow === "always" ||
      (streakShow === "untilComplete" && !goalMetToday)
    ) {
      preactSubviewManager.remove("streak");
      $("#streak").remove();
      $("#title").after('<div id="streak" class="titleBarWidget"></div>');
      preactSubviewManager.add(
        "streak",
        preact.h(StreakView, {
          model: models.streak,
          hideUponCompletion: streakShow === "untilComplete",
        }),
        $("#streak")[0]
      );
    } else {
      subviewManager.removeSubview("streak");
      preactSubviewManager.remove("streak");
    }
  }
};

// for when the page is no longer in view
export const close = () => {
  trackFinishPage();

  if (readAloudButton?.isPlaying) {
    readAloudButton.toggle();
  }
  ttsPosition = undefined;

  document.removeEventListener("visibilitychange", onVisibilityChange);

  $(window).off("resize", handleResize);

  if (body.attr("id") === pageName) {
    body.removeAttr("id");
  }

  models.bookModel.off();

  readerViewModel.destroy();
  readerViewModel = null;

  removeSideBarViews();

  for (const className of ["colorDark", "colorSepia"]) {
    body.removeClass(className);
  }

  subviewManager.removeSubviews();
  preactSubviewManager.removeAll();

  body.removeAttr("data-clickMode");

  spanTranslator.close();
  currentDictionaryHtml = undefined;
  previousUserWord = undefined;
  sideBarVisible = false;

  eventManager.stopListening();

  clearInterval(timerInterval);

  // Reset browser TTS
  browserTTSVoices.offChange(ttsVoiceChangedHandler);
  ttsVoices = undefined;
  ttsVoiceSelected = false;
};

var currentDictionaryHtml;
var currentDictionaryURL;
var sideBarVisible = false;

let previousUserWord;

const monolingualLookup = (word, span?) => {
  const embeddedDictionary = languageMap.getEmbeddedDictionary(
    models.bookModel.get("language"),
    models.bookModel.get("language"),
    word,
    // XXX populate context
    undefined,
    models.user
  );

  currentDictionaryHtml =
    (embeddedDictionary && embeddedDictionary.html) ||
    "<p>Sorry, monolingual dictionary not available for " +
      languageMap.map[models.bookModel.get("language")].english +
      ".</p>";
  currentDictionaryURL = embeddedDictionary && embeddedDictionary.URL;

  // TODO: external dictionary window
  if (!sideBarVisible) {
    toggleSidebar(() => {
      updateLayout(span, false);
    });
  } else {
    updateLayout(span, false);
  }

  const smartDictionaryTab = $(
    '.sideBar .tabHeader[data-tab="smartDictionary"]'
  );
  if (smartDictionaryTab.hasClass("selected")) {
    smartDictionaryTab.click();
  } else {
    $('.sideBar .tabHeader[data-tab="dictionary"]').click();
  }
};

/**
 * Returns true if shown, false if not.
 */
const showSmartDictionaryIfAppropriate = async (newContext?: string) => {
  if (!smartDictionaryVisible()) {
    return false;
  }

  if (await readlangClient.isLocalMode()) {
    preactSubviewManager.remove("smartDictionary");
    $("#smartDictionaryContainer").html(
      `<p class="smartDictionaryContent trialMode">
          <a href="#" class="signup">Sign up for free</a> for AI-powered context-aware explanations of words and phrases.
        </p>`
    );

    $(".smartDictionaryContent .signup").click(() => {
      signupModal.show({
        headingHtml: "<h2>Sign up for context explanations</h2>",
        models: models,
      });
      return false;
    });
    return false;
  }

  const userWord = previousUserWord;
  if (!userWord) {
    preactSubviewManager.remove("smartDictionary");
    $("#smartDictionaryContainer").html(
      `<p class="smartDictionaryContent">Click any word on the left for a context-aware explanation.</p>`
    );
    return false;
  }

  const currentContext =
    newContext ??
    _.last(
      userWord
        .get("contexts")
        .filter(
          (context) =>
            context.bookID &&
            (context.bookID === models.bookModel.id.toString() ||
              context.bookID._id.toString() === models.bookModel.id.toString())
        )
    ) ??
    previousNewContext;

  const newSmartDictionaryProps = {
    user: models.user,
    userWord,
    context: `${currentContext ? currentContext.text : undefined}${
      models.user.hasFeature("explainTitleAndAuthor")
        ? ` (from "${models.bookModel.get("title")}" ${
            models.bookModel.get("author")
              ? `by ${models.bookModel.get("author")}`
              : ""
          })`
        : ""
    }`,
    monolingualMode: models.user.get("readerTranslations") === "off",
  };
  // Only re-render the smart dictionary if the properties have changed
  if (!_.isEqual(oldSmartDictionaryProps, newSmartDictionaryProps)) {
    preactSubviewManager.remove("smartDictionary");
    $("#smartDictionaryContainer").empty();
    preactSubviewManager.add(
      "smartDictionary",
      preact.h(
        SmartDictionaryView as unknown as preact.FunctionComponent<any>,
        newSmartDictionaryProps
      ),
      $("#smartDictionaryContainer")[0]
    );
    oldSmartDictionaryProps = newSmartDictionaryProps;
  }

  // Update if translation was edited in the Words tab
  models.remoteUserWords.off("change:translation", onChangeTranslation);
  models.remoteUserWords.on("change:translation", onChangeTranslation);

  return true;
};

const onChangeTranslation = (model) => {
  if (model.id === previousUserWord?.id) {
    previousUserWord = model;
    showSmartDictionaryIfAppropriate();
  }
};

/** @type {{userWord?: any, newContext?: string}} */
const updateDictionary = (innerOptions) => {
  let userWord = innerOptions?.userWord ?? previousUserWord;

  if (!userWord) {
    return;
  }

  previousUserWord = userWord;

  if (userWord.get("language") !== models.bookModel.get("language")) {
    userWord.save(
      { language: models.bookModel.get("language") },
      { patch: true }
    );
  }

  showSmartDictionaryIfAppropriate(innerOptions?.newContext);

  if (models.user.get("readerTranslations") === "off") {
    monolingualLookup(userWord.get("word") /* TODO: add spanToRetain */);
    return;
  }

  const embeddedDictionary = languageMap.getEmbeddedDictionary(
    userWord.get("language"),
    models.user.get("firstLanguage"),
    userWord.get("word"),
    // XXX Is the last context guaranteed to be the one we want?
    _.last(userWord.get("contexts"))?.text ?? previousNewContext,
    models.user
  );

  currentDictionaryHtml =
    embeddedDictionary.html ||
    "<p>Sorry, we don't have a dictionary between your two languages</p>";
  currentDictionaryURL = embeddedDictionary.URL;

  if (
    embeddedDictionary.target &&
    embeddedDictionary.target !== models.user.get("firstLanguage")
  ) {
    $("#fallback")
      .text(
        "(Fallback to " +
          languageMap.map[embeddedDictionary.target].english +
          " below)"
      )
      .show();
  } else {
    $("#fallback").hide();
  }

  if (externalDictionaryWindow && !externalDictionaryWindow.closed) {
    externalDictionaryWindow.location.href = embeddedDictionary.URL;
  }

  showDictionaryIfVisible();
};

const showDictionaryIfVisible = () => {
  if (!dictionaryVisible()) {
    return;
  }

  const dictionaryContainer = $("#dictionaryContainer");
  if (currentDictionaryHtml) {
    dictionaryContainer.empty();
    dictionaryContainer.append(currentDictionaryHtml);
    dictionaryContainer.find("iframe").ready(updateDictionarySize);
    walkthrough.setAchievement("readerSidebar", true);
  }

  if (currentDictionaryURL) {
    const openDictionaryButton = $("#openDictionary");

    if (!openDictionaryButton.is(":visible")) {
      openDictionaryButton.show().click(() => {
        externalDictionaryWindow = window.open(
          currentDictionaryURL,
          "_blank",
          `location=no,menubar=no,toolbar=no,scrollbars=yes,width=${sideBarWidth},height=550`
        );
      });
    }
  }

  if (!subviewManager.subviews.toLanguageSelectView) {
    subviewManager.addSubview(
      "fromLanguageSelectView",
      new LanguageSelectView({
        learningLanguages: true,
        el: "#fromLanguage",
        model: models.user,
        field: "currentLearningLanguage",
        hint: isOwner()
          ? "Set the language of this document"
          : "Only the owner of the article can set its language",
        onChange: function (language) {
          models.bookModel.save({ language }, { patch: true });
          spanTranslator.setSourceLanguage(language);
        },
        flipIfSame: true,
        disabled: !isOwner(),
        english: true,
        // XXX It is possible for the public value to change while the user is on the page, in
        // which case the confirmationPrompt will not be correct. (Not likely to happen that often.)
        confirmationPrompt: models.bookModel.get("public")
          ? "This will change the language of the text for everyone. Are you sure?"
          : undefined,
      })
    );
    subviewManager.addSubview(
      "toLanguageSelectView",
      new LanguageSelectView({
        el: "#toLanguage",
        model: models.user,
        field: "firstLanguage",
        hint: "Set your first language",
        switchToMonolingualIfSame: true,
        english: true,
      })
    );
    subviewManager.addSubview(
      "translationDirectionArrowView",
      new TranslationDirectionArrowView({
        el: ".translationDirectionArrowView",
        model: models.user,
      })
    );
  }
};

const dictionaryVisible = () => {
  if (
    sideBarVisible &&
    $('.tabContent[data-tab="dictionary"]').hasClass("selected")
  ) {
    return true;
  } else {
    return false;
  }
};

const smartDictionaryVisible = () => {
  if (
    sideBarVisible &&
    $('.tabContent[data-tab="smartDictionary"]').hasClass("selected")
  ) {
    return true;
  } else {
    return false;
  }
};

const updateDictionarySize = () => {
  const iframe = $("#wordReferenceIFrame");
  let scale = 1;
  if (/matrix/.test(iframe.css("transform"))) {
    scale = 0.7;
  }
  const height =
    ($(window).height() -
      $(".sideBar .titleBar").height() -
      $("#dictionaryChoice").height()) /
      scale -
    4 /* borders */ +
    "px";
  $("#dictionaryContainer").css({
    height: height,
  });
  iframe.css({
    height: height,
    top: -iframe.height() * 0.5 * (1 - scale) + "px",
  });
};

const showContentTab = async () => {
  const sidebar = $(".sideBar");
  sidebar
    .find(
      '.tabContent[data-tab="' +
        sidebar.find(".tabHeader.selected").attr("data-tab") +
        '"]'
    )
    .addClass("selected");

  showDictionaryIfVisible();
  showSmartDictionaryIfAppropriate();
  removeSideBarViews();

  const selectedTab = sidebar.find(".tabHeader.selected").attr("data-tab");

  splitTest.trackEventWithProperties("showSidebarTab", {
    tab: selectedTab,
  });

  switch (selectedTab) {
    case "edit":
      if (isOwner()) {
        subviewManager.addSubview(
          "editDocumentView",
          new EditDocumentView({
            model: models.bookModel,
            user: models.user,
            refreshPage: function (refreshOptions) {
              if (refreshOptions && refreshOptions.reInitialize) {
                close();
              }
              render(containerElement, renderedCallback, appRouter, options);
            },
            notificationBar: notificationBar,
          })
        );
        $('.tabContent[data-tab="edit"]')
          .empty()
          .append(subviewManager.subviews.editDocumentView.$el);
      }
      break;
    case "editWord":
      if (!(await localAPI.isLocalMode())) {
        models.remoteUserWords.fetch({
          data: {
            bookID: options.bookID,
          },
        });
      }
      break;
    default:
      break;
  }
};

let clickToCancelTimeout;

const dismissSidebarHint = () => {
  $("#sideBarButtonContainer .tooltip").hide();
  $("#sideBarButtonContainer, .tabHeader").removeClass("glow");
  walkthrough.setAchievement("readerSidebar", true);
};

const removeSideBarViews = () => {
  if (subviewManager?.subviews?.editDocumentView) {
    subviewManager.subviews.editDocumentView.remove();
    subviewManager.subviews.editDocumentView = undefined;
  }
};

const toggleSidebar = (callback?: () => void) => {
  const sideBar = $(".sideBar");
  const container = $(".container");
  const toggleButton = $("#sideBarButton");
  const time = 200;

  sidebarAutoToggled = true;

  dismissSidebarHint();
  removeSideBarViews();
  $("#streak").show();

  if (sideBarVisible) {
    clearTimeout(clickToCancelTimeout);
    $("#sideBarButtonContainer .tooltip").hide();

    sideBar.animate({ right: `-${sideBarWidth}px` }, time);
    container.animate({ right: 0 }, time);
    toggleButton.attr("title", "Show Sidebar");
    setTimeout(() => {
      sideBar.find(".tabContent").removeClass("selected");
      sideBar.hide();
    }, time);
    sideBarVisible = false;
    $("#titleBar").append($("#sideBarButtonContainer"));

    $(".titleBarWidget").show();
    $("#mediaPanel").fadeIn();
    $("body").removeClass("sideBarVisible");
  } else {
    // Don't show the close hint on small screens since it doesn't fit
    if ($(window).width() > 754 && showSideBarCloseHint) {
      showSideBarCloseHint = false;
      clickToCancelTimeout = setTimeout(() => {
        $("#sideBarButtonContainer .tooltip h3").text("Sidebar");
        $("#sideBarButtonContainer .tooltip p").text(
          "Explore the other options in the sidebar, and click ☰ again to close."
        );
        $("#sideBarButtonContainer .tooltip").css("right", "480px");
        $("#sideBarButtonContainer .tooltip").fadeIn();

        // Disable this glow for now since it doesn't look good
        // $("#sideBarButton, .tabHeader:not(.selected)").addClass("glow");
      }, 4000);
    }

    let width = `${sideBarWidth}px`;
    if ($(window).width() <= 754) {
      $(".titleBarWidget").hide();
    }

    if ($(window).width() <= 600) {
      sideBar.find(".titleBar").append($("#sideBarButtonContainer"));
      width = "100%";
      $("#mediaPanel").fadeOut();
    } else {
      container.animate({ right: `${sideBarWidth}px` }, time);
    }

    sideBar.css({ width: width });
    sideBar.animate({ right: 0 }, time);
    sideBar.show();
    toggleButton.attr("title", "Hide Sidebar");
    showContentTab();

    sideBarVisible = true;
    showDictionaryIfVisible();
    showSmartDictionaryIfAppropriate();
    $("body").addClass("sideBarVisible");
  }

  if (sidebarSettings.persistentMode()) {
    sidebarSettings.setExpanded(sideBarVisible);
  }

  setTimeout(callback || updateLayout, time);
};

const mediaPanelHeight = () => $("#mediaPanel").height();

export const setMediaPanelHeight = (height) => {
  $("#mediaPanel").css({
    height: height,
  });
  $("#bookContent").css({
    top: height + 40,
  });
  updateLayout();
};

// TODO: review before enabling again - VIEWPORT_CSS
const hideFooter = () => {
  $("#footer").hide();
  $("#pageTextViewport").addClass("noFooter");
  updateLayout();
};

const showFooter = () => {
  $("#footer").show();
  $("#pageTextViewport").removeClass("noFooter");
  viewportCssBottom = $("#footer").height();
  updateLayout();
};

/**
 * Last position read within this session (used if no userBook is available)
 * @type {undefined | number}
 */
let lastPositionSession = undefined;

const setSyncAudioPosition = (position) => {
  const userBook = models.bookModel.get("userBook");
  const logEveryXWords = 30;

  let lastPosition;

  if (userBook) {
    lastPosition = userBook.position ?? 0;
  } else {
    lastPosition = lastPositionSession ?? 0;
  }

  if (
    Math.abs(lastPosition - position) <
    logEveryXWords /* && position < models.bookModel.get('totalWords') - 1*/
  ) {
    return;
  }

  if (userBook) {
    updateUserBookPosition(models.bookModel, position, undefined);
  } else {
    lastPositionSession = position;
  }

  const span = phraseHighlighter.getSpans([position]);
  if (span && readingTracker) {
    trackReadingInteraction(span);
    logWordsRead(readingTracker.getStats());

    // Reset readingTracker so as not to double count
    resetReadingTracker(position);
  }
};

const resetReadingTracker = (position: number) => {
  readingTracker = new ReadingTracker(
    position,
    Date.now(),
    languageMap.map[models.bookModel.get("language")].noWordBoundaries
      ? 0.5
      : models.bookModel.get("metrics")?.words
        ? models.bookModel.get("metrics").words /
          models.bookModel.get("totalWords")
        : 1
  );
};

export const showHeader = false;

export const getReaderViewModel = () => readerViewModel;
